<template>
  <block-item-card
    :min-height="353"
    :height="353"
    :title="$t('ALM.Section.Liquidity.Title')"
    :tooltip="$t('ALM.Section.Liquidity.Tooltip')"
    :title-info="$t('ALM.Section.Liquidity.TitleInfo')"
  >
    <div class="liquidity-container">
      <div class="liquidity-left-panel">
        <div class="liquidity-left-panel__top text-size-14 font-weight-medium synthesis-brand-dark-blue--text">
          <span v-html="$t('ALM.Section.Liquidity.LiquidityCoverage').replace(' ', '<br>')" />
          <fraction align="center">
            <template #top>
              <span>{{ $t('ALM.Section.Liquidity.Loans') }}</span>
            </template>
            <template #bottom>
              <span>{{ $t('ALM.Section.Liquidity.Deposits') }}</span>
            </template>
          </fraction>
        </div>
        <div class="liquidity-left-panel__bottom">
          <div
            class="liquidity-left-panel__bottom__chart"
            style="width: 60px;"
          >
            <div
              class="synthesis-text text-size-24 font-weight-medium synthesis-brand-dark-blue--text text-center"
              style="margin-bottom:26px;"
            >
              {{ almSummary && almSummary.liquidityCoverageFmt }}
            </div>
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <base-e-chart-bar
                :axis-y-label-inside="false"
                :axis-y-label-interval="1"
                :axis-y-label-show="false"
                :axis-y-split-line-show="false"
                :axis-y-split-line-type="[8, 8]"
                axis-x-label-value-format="number"
                :chart-data-options="liquidityCoverageChartData"
                :chart-grid-margin="{left: 0,top: 0,right: 0,bottom: 0,containLabel: false}"
                :chart-height="200"
                :bar-width="160"
                :bar-max-width="'200%'"
                :show-series-label="false"
                chart-animation-easing="quinticOut"
                :bar-color="themeColorRGBA('synthesis-ui-yellow-01')"
                :tooltip-formatter="liquidityCoverageTooltipFormatter"
                :silent="false"
              />
            </v-lazy>
          </div>
          <div
            class="liquidity-left-panel__bottom__chart"
            style="width: 90px;"
          >
            <div class="synthesis-text text-center text-size-24 font-weight-medium synthesis-brand-dark-blue--text mb-3">
              {{ almSummary && almSummary.loansVsDepositsFactorFmt }}
            </div>
            <div
              class="chart-labels d-flex justify-space-between mb-1 justify-center"
            >
              <span
                style="width:100px"
                class="text-size-10 synthesis-brand-dark-blue--text text-center"
              >{{ $t('ALM.Section.Liquidity.Loans') }}</span>
              <span
                style="width:100px"
                class="text-size-10 synthesis-brand-dark-blue--text text-center"
              >{{ $t('ALM.Section.Liquidity.Deposits') }}</span>
            </div>
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <base-e-chart-bar
                :axis-y-label-inside="false"
                :axis-y-label-interval="1"
                :axis-y-label-show="false"
                :axis-y-split-line-show="false"
                :axis-y-split-line-type="[8, 8]"
                axis-x-label-value-format="number"
                :chart-data-options="loansChartData"
                :chart-grid-margin="{left: 0,top: 0,right: 0,bottom: 0,containLabel: false}"
                :chart-height="200"
                chart-animation-easing="quinticOut"
                :show-series-label="false"
                :chart-width="300"
                :bar-max-width="'100%'"
                :bar-width="'100%'"
                :bar-color="[themeColorRGBA('synthesis-ui-green-02'), themeColorRGBA('synthesis-ui-orange-02')]"
                :tooltip-formatter="loansVsDepositsTooltipFormatter"
                :silent="false"
              />
            </v-lazy>
          </div>
        </div>
      </div>

      <div class="liquidity-right-panel">
        <div>
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="synthesis-inter-text text-size-12 synthesis-brand-dark-blue--text">
              {{ $t('ALM.Section.Liquidity.LiquidAssets') }} / {{ $t('ALM.Section.Liquidity.TotalAssets') }}
            </div>
            <div class="synthesis-text text-size-20 font-weight-medium synthesis-brand-dark-blue--text">
              {{ almSummary && almSummary.liquidVsTotalAssetsFmt }}
            </div>
          </div>
          <v-progress-linear
            :value="almSummary && almSummary.liquidVsTotalAssets"
            color="synthesis-ui-green-02"
            height="8"
            rounded
            background-color="white"
          />
        </div>
        <div>
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="synthesis-inter-text text-size-12 synthesis-brand-dark-blue--text">
              {{ $t('ALM.Section.Liquidity.CoreDeposits') }} / {{ $t('ALM.Section.Liquidity.TotalDeposits') }}
            </div>
            <div class="synthesis-text text-size-20 font-weight-medium synthesis-brand-dark-blue--text">
              {{ almSummary && almSummary.coreVsTotalDepositsFmt }}
            </div>
          </div>
          <v-progress-linear
            :value="almSummary && almSummary.coreVsTotalDeposits"
            color="synthesis-ui-orange-02"
            height="8"
            rounded
            background-color="white"
          />
        </div>
        <div>
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="synthesis-inter-text text-size-12 synthesis-brand-dark-blue--text">
              {{ $t('ALM.Section.Liquidity.UninsuredDeposits') }} / {{ $t('ALM.Section.Liquidity.TotalDeposits') }}
            </div>
            <div class="synthesis-text text-size-20 font-weight-medium synthesis-brand-dark-blue--text">
              {{ almSummary && almSummary.uninsuredVsTotalDepositsFmt }}
            </div>
          </div>
          <v-progress-linear
            :value="almSummary && almSummary.uninsuredVsTotalDeposits"
            color="synthesis-ui-orange-02"
            height="8"
            rounded
            background-color="white"
          />
        </div>
      </div>
    </div>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import BaseEChartBar from '@/theme/default/components/charts/echarts/BaseEChartBar'
import baseEChartColors from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import Fraction from '@/theme/default/components/common/Fraction'
import { toNumberFormatNotationShort } from '@/lib/utils/helper'

export default {
  name      : 'Liquidity',
  components: {
    BlockItemCard,
    BaseEChartBar,
    Fraction
  },
  mixins   : [baseEChartColors],
  props    : {},
  dataStore: {
    ALMSummary: 'ALM.ALMSummary.Data'
  },
  computed: {
    almSummary () {
      return this.ALMSummary || {}
    },
    liquidityCoverageChartData () {
      const coverage = this.almSummary && this.almSummary.liquidityCoverage || 0

      return [
        { x: ['coverage'], y: [coverage] }
      ]
    },
    currencySymbol () {
      return this.almSummary?.currencySymbol || ''
    },
    loans () {
      return this.almSummary && this.almSummary.Liquidity?.loans || 0
    },
    deposits () {
      return this.almSummary && this.almSummary.Liquidity.deposits || 0
    },
    loansChartData () {
      const factor = this.almSummary && this.almSummary.loansVsDepositsFactor || 0

      return [
        { x: ['loans', 'deposits'], y: [factor * 100, 100] }
      ]
    },
    liquidityCoverageTooltipFormatter () {
      return (params) => {
        const coverageFmt = this.almSummary && this.almSummary.liquidityCoverageFmt || '0%'
        const param = Array.isArray(params) ? params[0] : params

        return `<div style="font-weight: bold; margin-bottom: 8px;">${this.$t('ALM.Section.Liquidity.LiquidityCoverage')}</div>
                <div style="margin: 4px 0;">
                  <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; margin-right: 8px; border-radius: 2px;"></span>
                  <span>${this.$t('ALM.Section.Liquidity.LiquidityCoverage')}: ${coverageFmt}</span>
                </div>`
      }
    },
    loansVsDepositsTooltipFormatter () {
      return (params) => {
        const param = Array.isArray(params) ? params[0] : params

        let label = ''
        let value = ''
        if (param.name === 'loans') {
          label = this.$t('ALM.Section.Liquidity.Loans')
          value = this.loans
        } else if (param.name === 'deposits') {
          label = this.$t('ALM.Section.Liquidity.Deposits')
          value = this.deposits
        }

        value = this.currencySymbol + toNumberFormatNotationShort(value)

        return `<div style="font-weight: bold; margin-bottom: 8px;">${label}</div>
                <div style="margin: 4px 0;">
                  <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; margin-right: 8px; border-radius: 2px;"></span>
                  <span>${label}: ${value}</span>
                </div>`
      }
    }

  }
}
</script>

<style scoped>
.liquidity-container {
  display: flex;
  flex-direction: row;
  height: 353px;
  min-height: 353px;
  padding: 0 16px;
  justify-content: space-between;
  align-items: stretch;
  flex: 1;
  position: relative;
  gap: 40px;
}

.liquidity-left-panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 20px;
}

.liquidity-right-panel {
  flex: 1;
  background-color: #F7F9FC;
  padding: 20px;
  box-shadow: 0px 4px 16px 0px #6E8EDF52;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: stretch;
  height: 332px;
  min-height: 332px;
}

.dual-vertical-bars {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 0 auto;
  width: fit-content;
}

.bar-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.bar-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.liquidity-left-panel__top,
.liquidity-left-panel__bottom {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 40px;
}
</style>
